import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import SimpleAlert from "../../../../../../components/display/SimpleAlert";
import { GlobalDataContext } from "../../../../../../contexts/GlobalData";
import {
  GET_WAREHOUSE_CATEGORIES,
  GET_WAREHOUSE_SUPPLIERS,
} from "../../../../../../enums/ApiPaths";
import {
  DELETE_WAREHOUSE_ITEM,
  EDIT_WAREHOUSE_ITEM,
  VIEW_AUDITING,
} from "../../../../../../enums/PermissionTitles";
import {
  WAREHOUSE_CATEGORIES,
  WAREHOUSE_SUPPLIERS,
} from "../../../../../../enums/QueryKeys";
import { useFetch } from "../../../../../../hooks/Fetching";
import useUser from "../../../../../../hooks/useUser";
import { useWarehouseLabelBuffer } from "../../../../../../contexts/WarehouseLabelBuffer";
import { useWarehouseShoppingListBuffer } from "../../../../../../contexts/WarehouseShoppingListBuffer";
import { WAREHOUSE_TABLE_PARAMS } from "../ClinicWarehousePage";
import WarehouseItemFormDialog from "../form/WarehouseItemFormDialog";
import { useGetClinicWarehouseItems } from "../hooks/useGetClinicWarehouseItems";
import { useGetWarehousePreferences } from "../hooks/useGetWarehousePreferences";
import DeleteWarehouseItemDialog from "./DeleteWarehouseItemDialog";
import WarehouseFilters, { WAREHOUSE_FILTER_ALL } from "./WarehouseFilters";
import WarehouseItemAuditingDialog from "./WarehouseItemAuditingDialog";
import WarehouseItemDetailsDialog from "./WarehouseItemDetailsDialog";
import WarehouseTable from "./WarehouseTable";

const DEFAULT_SORT_FIELD = "title";
const DEFAULT_SORT_DIRECTION = "asc";
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;

const WarehouseItemsTable = React.forwardRef(function WarehouseItemsTable(
  props,
  ref,
) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { roles, currentClinic } = useUser();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { remove: removeFromLabelQueue } = useWarehouseLabelBuffer();
  const { remove: removeFromShoppingList } = useWarehouseShoppingListBuffer();

  const clinicId = roles.master ? id : currentClinic.id;
  const { data: warehousePreferences } = useGetWarehousePreferences(clinicId);
  const currency = warehousePreferences?.currency ?? "EUR";

  const isEditingItemsAllowed = isPermissionAllowed(EDIT_WAREHOUSE_ITEM);
  const isDeletingItemsAllowed = isPermissionAllowed(DELETE_WAREHOUSE_ITEM);
  const isViewingAuditingAllowed = isPermissionAllowed(VIEW_AUDITING);

  const search = searchParams.get(WAREHOUSE_TABLE_PARAMS.search) || "";
  const categoryId =
    searchParams.get(WAREHOUSE_TABLE_PARAMS.category) || WAREHOUSE_FILTER_ALL;
  const supplierId =
    searchParams.get(WAREHOUSE_TABLE_PARAMS.supplier) || WAREHOUSE_FILTER_ALL;
  const sortParam = searchParams.get(WAREHOUSE_TABLE_PARAMS.sort) || "";
  const [sortField, sortDirection] = sortParam
    ? sortParam.split("=")
    : [DEFAULT_SORT_FIELD, DEFAULT_SORT_DIRECTION];
  const page =
    Number(searchParams.get(WAREHOUSE_TABLE_PARAMS.page)) || DEFAULT_PAGE;
  const size =
    Number(searchParams.get(WAREHOUSE_TABLE_PARAMS.size)) || DEFAULT_PAGE_SIZE;

  const {
    data,
    isLoading,
    isFetching: isItemsFetching,
    error,
  } = useGetClinicWarehouseItems(clinicId, {
    categoryId: categoryId !== WAREHOUSE_FILTER_ALL ? categoryId : null,
    supplierId: supplierId !== WAREHOUSE_FILTER_ALL ? supplierId : null,
    search,
    sort: sortParam || null,
    page,
    size,
  });

  const { data: categoriesData, error: categoriesError } = useFetch(
    GET_WAREHOUSE_CATEGORIES + clinicId,
    null,
    [WAREHOUSE_CATEGORIES, clinicId],
    Boolean(clinicId),
  );
  const { data: suppliersData, error: suppliersError } = useFetch(
    GET_WAREHOUSE_SUPPLIERS + clinicId,
    null,
    [WAREHOUSE_SUPPLIERS, clinicId],
    Boolean(clinicId),
  );

  const items = data?.content ?? [];
  const totalCount = data?.totalElements ?? 0;

  const [formState, setFormState] = React.useState(null);
  const [itemPendingDelete, setItemPendingDelete] = React.useState(null);
  const [detailsItemId, setDetailsItemId] = React.useState(null);
  const [auditingItem, setAuditingItem] = React.useState(null);
  const detailsItem = items.find((item) => item.id === detailsItemId) ?? null;

  const categories = React.useMemo(() => {
    return [...(categoriesData ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [categoriesData]);

  const suppliers = React.useMemo(() => {
    return [...(suppliersData ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [suppliersData]);

  const categoryNames = React.useMemo(() => {
    return Array.from(
      new Set(categories.map((category) => category.name)),
    ).sort();
  }, [categories]);

  const supplierNames = React.useMemo(() => {
    return Array.from(
      new Set(suppliers.map((supplier) => supplier.name)),
    ).sort();
  }, [suppliers]);

  const hasActiveFilters =
    Boolean(search) ||
    categoryId !== WAREHOUSE_FILTER_ALL ||
    supplierId !== WAREHOUSE_FILTER_ALL;

  const isEmptyWarehouse = !isLoading && totalCount === 0 && !hasActiveFilters;

  // Lets the parent page (which renders the "Add" button in its shared
  // header) trigger the add-item dialog without owning any item state.
  React.useImperativeHandle(ref, () => ({
    openAddDialog: () => setFormState({ item: null }),
  }));

  function setParam(key, value, { resetPage = false } = {}) {
    const params = new URLSearchParams(searchParams);
    if (!value || value === WAREHOUSE_FILTER_ALL) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    if (resetPage) {
      params.delete(WAREHOUSE_TABLE_PARAMS.page);
    }
    setSearchParams(params);
  }

  function handleSearchChange(value) {
    setParam(WAREHOUSE_TABLE_PARAMS.search, value, { resetPage: true });
  }

  function handleCategoryChange(value) {
    setParam(WAREHOUSE_TABLE_PARAMS.category, value, { resetPage: true });
  }

  function handleSupplierChange(value) {
    setParam(WAREHOUSE_TABLE_PARAMS.supplier, value, { resetPage: true });
  }

  function handleSort(field, direction) {
    setParam(WAREHOUSE_TABLE_PARAMS.sort, `${field}=${direction}`, {
      resetPage: true,
    });
  }

  function handlePageChange(newPage) {
    setParam(WAREHOUSE_TABLE_PARAMS.page, newPage);
  }

  function handleRowsPerPageChange(newSize) {
    setParam(WAREHOUSE_TABLE_PARAMS.size, newSize, { resetPage: true });
  }

  function handleClearFilters() {
    const params = new URLSearchParams(searchParams);
    params.delete(WAREHOUSE_TABLE_PARAMS.search);
    params.delete(WAREHOUSE_TABLE_PARAMS.category);
    params.delete(WAREHOUSE_TABLE_PARAMS.supplier);
    params.delete(WAREHOUSE_TABLE_PARAMS.page);
    setSearchParams(params);
  }

  function closeItemForm() {
    setFormState(null);
  }

  if (error || categoriesError || suppliersError) {
    return (
      <SimpleAlert
        message="ErrorFetchingWarehouseItemsData"
        severity="error"
      />
    );
  }

  return (
    <>
      {!isEmptyWarehouse && (
        <WarehouseFilters
          search={search}
          onSearchChange={handleSearchChange}
          category={categoryId}
          onCategoryChange={handleCategoryChange}
          categories={categories}
          supplier={supplierId}
          onSupplierChange={handleSupplierChange}
          suppliers={suppliers}
          onClearFilters={handleClearFilters}
        />
      )}

      <WarehouseTable
        items={items}
        currency={currency}
        onRowClick={(item) => setDetailsItemId(item.id)}
        order={sortDirection}
        orderBy={sortField}
        onSort={handleSort}
        isLoading={isLoading || (isItemsFetching && items.length === 0)}
        emptyMessage={
          hasActiveFilters
            ? "NoWarehouseItemsFoundForGivenQuery"
            : "NoWarehouseItems"
        }
        page={page}
        rowsPerPage={size}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <WarehouseItemDetailsDialog
        item={detailsItem}
        currency={currency}
        canEdit={isEditingItemsAllowed}
        canDelete={isDeletingItemsAllowed}
        canViewAuditing={isViewingAuditingAllowed}
        onClose={() => setDetailsItemId(null)}
        onEdit={(item) => {
          setDetailsItemId(null);
          setFormState({ item });
        }}
        onDelete={(item) => setItemPendingDelete(item)}
        onViewAuditing={(item) => setAuditingItem(item)}
      />

      <WarehouseItemAuditingDialog
        itemId={auditingItem?.id}
        itemTitle={auditingItem?.title}
        currency={currency}
        open={Boolean(auditingItem)}
        onClose={() => setAuditingItem(null)}
      />

      {formState && (
        <WarehouseItemFormDialog
          open={Boolean(formState)}
          onClose={closeItemForm}
          categories={categoryNames}
          suppliers={supplierNames}
          initialValues={
            formState.item
              ? {
                  ...formState.item,
                  category: formState.item.category?.name ?? "",
                  supplier: formState.item.supplier?.name ?? "",
                }
              : undefined
          }
          onSubmit={closeItemForm}
        />
      )}

      <DeleteWarehouseItemDialog
        item={itemPendingDelete}
        onCancel={() => setItemPendingDelete(null)}
        onDeleted={(deletedItem) => {
          setDetailsItemId(null);
          removeFromLabelQueue(deletedItem.id);
          removeFromShoppingList(deletedItem.id);
        }}
      />
    </>
  );
});

export default WarehouseItemsTable;